<!-- assignments.vue - CLEAN MODERN DESIGN -->
<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <div class="text-center">
      <!-- Week Navigation - Cleaner -->
      <div class="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <button
            @click="previousWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Previous
          </span>
        </button>
        <div class="text-center">
          <h3 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
            Week of {{ formatDate(currentWeekStart) }}
          </h3>
        </div>
        <button
            @click="nextWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            Next
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
        </button>
      </div>

      <!-- View Toggle - Cleaner Pills -->
      <div class="flex justify-center mb-8">
        <div class="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl inline-flex">
          <button
              @click="isWeekView = true"
              :class="[
              isWeekView
                ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400',
              'font-semibold py-2 px-6 rounded-lg transition-all duration-200'
            ]"
          >
            Week View
          </button>
          <button
              @click="isWeekView = false"
              :class="[
              !isWeekView
                ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400',
              'font-semibold py-2 px-6 rounded-lg transition-all duration-200'
            ]"
          >
            Day View
          </button>
        </div>
      </div>

      <!-- Day Navigation - Simplified -->
      <div v-if="!isWeekView" class="flex flex-wrap justify-center mb-8 gap-2">
        <button
            v-for="(day, index) in daysOfWeek"
            :key="index"
            @click="currentDayIndex = index"
            :class="[
            currentDayIndex === index
              ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
            'font-semibold py-2 px-5 rounded-lg transition-all duration-200 hover:shadow-md'
          ]"
        >
          {{ day }}
        </button>
      </div>

      <!-- Loading Spinner -->
      <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-slate-600 dark:border-indigo-600"></div>
      </div>

      <!-- Assignment List - Cleaner Container -->
      <div
          v-else
          class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50"
      >
        <h4 class="text-2xl sm:text-3xl font-bold mb-8 text-slate-900 dark:text-white">
          {{ isWeekView ? "Week's Assignments" : daysOfWeek[currentDayIndex] + "'s Assignments" }}
        </h4>

        <!-- Week View - Cleaner Cards -->
        <div v-if="isWeekView" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div
              v-for="(assignments, day, index) in weeklyAssignments"
              :key="day"
              class="flex flex-col rounded-xl"
          >
            <div class="relative bg-slate-700 dark:bg-indigo-600 text-white font-bold text-base py-2.5 px-4 rounded-t-xl">
              {{ day }}
              <!-- Sits on the card's top edge so it never collides with the centered day name -->
              <span
                  v-if="isTodayIndex(index)"
                  class="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] leading-4 font-semibold uppercase tracking-wide bg-indigo-500 dark:bg-white text-white dark:text-indigo-700 px-2 py-0.5 rounded-full shadow"
              >
                {{ highlightedDay.label }}
              </span>
            </div>
            <div class="bg-white dark:bg-slate-800 rounded-b-xl border-x border-b border-slate-200 dark:border-slate-700 min-h-[200px] p-3">
              <ul v-if="assignments.length" class="space-y-2.5">
                <li
                    v-for="assignment in assignments"
                    :key="assignment.assignment_id"
                    class="bg-slate-50 dark:bg-slate-900 hover:bg-white dark:hover:bg-slate-800 rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-slate-200 dark:border-slate-700"
                    @click="openModal(assignment)"
                >
                  <div class="flex flex-col">
                    <div class="flex-1 mb-2.5">
                      <h5 class="text-sm font-bold mb-1 text-slate-900 dark:text-white">
                        {{ assignment.section_name }}
                      </h5>
                      <p class="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                        {{ truncateText(assignment.short_description, 50) }}
                      </p>
                    </div>
                    <!-- Status Indicators - Simplified -->
                    <div class="flex gap-1.5 justify-center pt-2">
                      <button
                          v-for="status in statuses"
                          :key="status.value"
                          @click.stop="updateStatus(assignment, status.value)"
                          :class="[
                          'relative group/status flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-200',
                          assignment.status === status.value
                            ? `${status.bgClass} text-white`
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                        ]"
                      >
                        <!-- Not Started -->
                        <svg
                            v-if="status.value === 'not-started'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <!-- In Progress -->
                        <svg
                            v-else-if="status.value === 'in-progress'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <!-- Complete -->
                        <svg
                            v-else-if="status.value === 'complete'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        <!-- Tooltip -->
                        <span class="absolute bottom-full mb-2 w-max bg-slate-900 text-white text-xs rounded-lg py-1 px-2.5 opacity-0 group-hover/status:opacity-100 transition-opacity duration-200 pointer-events-none">
                          {{ status.label }}
                        </span>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
                <div class="text-center py-8">
                  <svg class="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-xs">No assignments</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Day View - Cleaner Cards -->
        <div v-else>
          <ul v-if="dailyAssignments.length" class="space-y-4">
            <li
                v-for="assignment in dailyAssignments"
                :key="assignment.assignment_id"
                class="bg-white dark:bg-slate-800 hover:shadow-lg rounded-xl p-5 shadow-md transition-all duration-200 cursor-pointer border border-slate-200 dark:border-slate-700"
                @click="openModal(assignment)"
            >
              <div class="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div class="flex-1 w-full">
                  <div class="flex items-start gap-3 mb-3">
                    <div class="w-1 h-12 bg-slate-600 dark:bg-indigo-600 rounded-full flex-shrink-0"></div>
                    <div class="flex-1">
                      <h5 class="text-lg font-bold mb-1.5 text-slate-900 dark:text-white">
                        {{ assignment.section_name }}
                      </h5>
                      <p class="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                        {{ truncateText(assignment.short_description, 100) }}
                        <span
                            v-if="assignment.short_description.length > 100"
                            @click.stop="openModal(assignment)"
                            class="text-slate-700 dark:text-indigo-400 hover:text-slate-900 dark:hover:text-indigo-300 font-semibold cursor-pointer ml-1 transition-colors"
                        >
                          Read More →
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col items-end gap-3 sm:min-w-[220px]">
                  <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span class="font-medium">{{ formatDate(assignment.date_due) }}</span>
                  </div>
                  <!-- Status Pills - Cleaner -->
                  <div class="flex gap-2">
                    <button
                        v-for="status in statuses"
                        :key="status.value"
                        @click.stop="updateStatus(assignment, status.value)"
                        :class="[
                        'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200',
                        assignment.status === status.value
                          ? `${status.bgClass} text-white shadow-sm`
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                      ]"
                    >
                      {{ status.label }}
                    </button>
                  </div>
                  <!-- Status Counts -->
                  <p v-if="assignment.status_counts && assignment.status_counts['complete']" class="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    <span>
                      {{ assignment.status_counts['complete'] }}
                      {{ pluralize(assignment.status_counts['complete'], 'person', 'people') }} completed
                    </span>
                  </p>
                  <!-- Delete Button -->
                  <button
                      v-if="assignment.user_task"
                      @click.stop="deleteAssignment(assignment)"
                      class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold text-xs flex items-center gap-1 transition-colors"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <div v-else class="text-center py-16">
            <svg class="w-16 h-16 mx-auto mb-3 text-slate-300 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-slate-500 dark:text-slate-400">No assignments due on this day</p>
          </div>
        </div>

        <!-- Add Custom Task Button - Cleaner -->
        <button
            @click="openAddTaskModal"
            class="mt-8 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 mx-auto"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Custom Task
        </button>
      </div>
    </div>

    <!-- Modal for Assignment Description - Cleaner -->
    <div
        v-if="showModal"
        class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4"
        @wheel.stop
    >
      <div
          class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-2xl w-full relative border border-slate-200 dark:border-slate-700 max-h-[85vh] overflow-hidden"
          @click.stop
          @touchmove.stop
      >
        <button
            @click="closeModal"
            class="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h3 class="text-2xl font-bold mb-1 text-slate-900 dark:text-white break-words pr-12">
          {{ currentAssignment.section_name }}
        </h3>
        <p class="text-base font-semibold text-slate-500 dark:text-slate-400 mb-6">
          {{ currentAssignment.short_description }}
        </p>

        <!-- Scrollable description -->
        <div
            class="desc-scroll text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words max-h-[45vh] overflow-y-auto pr-2 leading-relaxed"
        >
          {{ currentAssignment.long_description }}
        </div>

        <!-- Due date with class start time -->
        <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400 mt-6">
          <svg class="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span class="text-base">
            Due: {{ formatModalDueDate(currentAssignment.date_due) }}
            <span v-if="currentAssignment.classStartTime" class="text-slate-500 dark:text-slate-500">
              (class at {{ currentAssignment.classStartTime }})
            </span>
          </span>
        </div>

        <div class="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
          <button v-if="currentAssignment.user_task" @click="editAssignment(currentAssignment)"
              class="rounded-xl border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
            Edit assignment
          </button>
          <button
              v-if="currentAssignment.user_task"
              @click="deleteAssignment(currentAssignment); closeModal()"
              class="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Delete
          </button>
          <button
              @click="closeModal"
              class="bg-slate-700 hover:bg-slate-800 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <CustomAssignmentEditor v-if="showAddTaskModal" :assignment="editingAssignment" :token="token"
        @close="closeAddTaskModal" @saved="onAssignmentSaved" />
  </main>
</template>

<script>
import axios from 'axios';
import CustomAssignmentEditor from '@/components/CustomAssignmentEditor.vue';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import Swal from 'sweetalert2';
import { consumeTokenFromUrl, getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

export default {
  components: { CustomAssignmentEditor },
  data() {
    return {
      token: null,
      showModal: false,
      showAddTaskModal: false,
      currentAssignment: null,
      editingAssignment: null,
      assignments: [],
      // Cache for schedules by date (to look up class start times)
      schedulesCache: {},
      scheduleRequests: {},
      schedulePrefetchQueued: false,
      currentDayIndex: 0,
      currentWeekStart: null,
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      statuses: [
        {
          value: 'not-started',
          label: 'Not Started',
          activeClass: 'bg-red-700 text-white',
          inactiveClass: 'bg-red-50 text-red-600',
          activeColorClass: 'text-red-500',
          bgClass: 'bg-red-600',
        },
        {
          value: 'in-progress',
          label: 'In Progress',
          activeClass: 'bg-yellow-500 text-white',
          inactiveClass: 'bg-yellow-50 text-yellow-700',
          activeColorClass: 'text-yellow-500',
          bgClass: 'bg-amber-500',
        },
        {
          value: 'complete',
          label: 'Complete',
          activeClass: 'bg-green-700 text-white',
          inactiveClass: 'bg-green-50 text-green-700',
          activeColorClass: 'text-green-500',
          bgClass: 'bg-green-600',
        },
      ],
      isLoading: false,
      isWeekView: true,
      savedScrollY: 0,
    };
  },
  computed: {
    anyModalOpen() {
      return this.showModal || this.showAddTaskModal;
    },
    swalDarkModeConfig() {
      return {
        buttonsStyling: false,
        customClass: {
          popup: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl',
          title: 'text-slate-900 dark:text-white',
          htmlContainer: 'text-slate-700 dark:text-slate-300',
          confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg ml-2',
          cancelButton: 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white font-semibold py-2 px-4 rounded-lg mr-2',
          actions: 'gap-2'
        }
      };
    },
    highlightedDay() {
      // After 5pm Eastern, highlight tomorrow instead of today
      const now = new Date();
      const hourET = parseInt(
          new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }).format(now)
      );
      if (hourET >= 17) {
        return { date: this.formatApiDate(new Date(now.getTime() + 24 * 60 * 60 * 1000)), label: 'Tomorrow' };
      }
      return { date: this.formatApiDate(now), label: 'Today' };
    },
    dailyAssignments() {
      const currentDate = this.getDateByDay(this.currentWeekStart, this.currentDayIndex);
      const formattedDate = this.formatApiDate(currentDate);
      return this.assignments.filter((assignment) => {
        const assignmentDate = assignment.date_due.split('T')[0];
        return assignmentDate === formattedDate;
      });
    },
    weeklyAssignments() {
      const assignmentsByDay = {};
      for (let i = 0; i < this.daysOfWeek.length; i++) {
        const dayName = this.daysOfWeek[i];
        const currentDate = this.getDateByDay(this.currentWeekStart, i);
        const formattedDate = this.formatApiDate(currentDate);
        assignmentsByDay[dayName] = this.assignments.filter((assignment) => {
          const assignmentDate = assignment.date_due.split('T')[0];
          return assignmentDate === formattedDate;
        });
      }
      return assignmentsByDay;
    },
  },
  watch: {
    anyModalOpen(isOpen) {
      if (isOpen) this.lockBodyScroll();
      else this.unlockBodyScroll();
    },
  },
  created() {
    this.currentDayIndex = this.getCurrentDayIndex();
    this.currentWeekStart = this.getCurrentWeekStart();
  },
  methods: {
    lockBodyScroll() {
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, this.savedScrollY || 0);
    },
    getCurrentDayIndex() {
      const now = new Date();

      // Get current date/time in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      let dayOfWeek = weekdayMap[weekdayStr];

      // If after 4pm Eastern, advance to next day
      if (hourET >= 16) {
        if (dayOfWeek === 5) {
          dayOfWeek = 1; // Friday after 4pm -> Monday
        } else if (dayOfWeek === 6 || dayOfWeek === 0) {
          dayOfWeek = 1; // Weekend -> Monday
        } else {
          dayOfWeek += 1;
        }
      }

      if (dayOfWeek === 6 || dayOfWeek === 0) return 0;
      return dayOfWeek - 1;
    },
    getCurrentWeekStart() {
      const now = new Date();

      // Get current date/time components in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const yearET = parseInt(getPart('year'));
      const monthET = parseInt(getPart('month')) - 1; // 0-indexed for Date constructor
      const dayET = parseInt(getPart('day'));
      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      // Map weekday string to number (0=Sun, 1=Mon, ..., 6=Sat)
      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayOfWeekET = weekdayMap[weekdayStr];

      const isFridayAfter4pm = dayOfWeekET === 5 && hourET >= 16;
      const isWeekend = dayOfWeekET === 6 || dayOfWeekET === 0;

      let targetDay = dayET;

      if (isFridayAfter4pm || isWeekend) {
        const daysUntilNextMonday = (8 - dayOfWeekET) % 7 || 7;
        targetDay += daysUntilNextMonday;
      } else {
        const daysSinceMonday = (dayOfWeekET + 6) % 7;
        targetDay -= daysSinceMonday;
      }

      // Create date at noon UTC to avoid day boundary issues when converting timezones
      // 17:00 UTC = 12:00 noon Eastern (during EST) - safe middle of day
      const weekStartDate = new Date(Date.UTC(yearET, monthET, targetDay, 17, 0, 0, 0));

      return weekStartDate;
    },
    extractToken() {
      const token = consumeTokenFromUrl() || getActiveAuthToken();

      if (token) {
        this.token = token;
      } else {
        beginLogin({ originalUrl: window.location.href });
      }
    },
    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Error decoding token:', error);
        return true;
      }
    },
    async fetchAssignments(showLoading = true) {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        if (showLoading) this.isLoading = true;

        const start_date = this.formatApiDate(this.currentWeekStart);
        const end_date = this.formatApiDate(
            new Date(this.currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
        );

        const response = await axios.get(backendUrl('/assignments'), {
          params: { start_date, end_date },
          headers: { Authorization: `Bearer ${this.token}` },
        });

        // Clean HTML tags from assignment fields
        this.assignments = response.data.value.map(assignment => ({
          ...assignment,
          short_description: this.cleanAssignmentDescription(assignment.short_description),
          long_description: this.cleanAssignmentDescription(assignment.long_description),
          section_name: this.cleanAssignmentDescription(assignment.section_name)
        }));
      } catch (error) {
        console.error('Error fetching assignments:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      } finally {
        if (showLoading) this.isLoading = false;
        this.$nextTick(() => {
          this.queueSchedulePrefetch();
        });
      }
    },
    nextWeek() {
      // Add 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchAssignments();
    },
    previousWeek() {
      // Subtract 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchAssignments();
    },
    formatDate(dateInput) {
      let date = dateInput;
      if (!(dateInput instanceof Date)) date = new Date(dateInput);

      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    formatModalDueDate(dateString) {
      if (!dateString) return '';
      // Parse the date string (YYYY-MM-DD or ISO format)
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'
      });
    },
    formatTime(timeString) {
      // Parse ISO string and display in Eastern timezone
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    formatApiDate(date) {
      // Format date in Eastern timezone for API calls
      const easternFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      // en-CA gives YYYY-MM-DD format
      return easternFormatter.format(date);
    },
    isTodayIndex(dayIndex) {
      if (!this.currentWeekStart) return false;
      return this.formatApiDate(this.getDateByDay(this.currentWeekStart, dayIndex)) === this.highlightedDay.date;
    },
    getDateByDay(weekStart, dayIndex) {
      // Add days to the week start date
      // weekStart is stored at 17:00 UTC (noon Eastern), so adding days keeps us at same time
      const date = new Date(weekStart.getTime() + dayIndex * 24 * 60 * 60 * 1000);
      return date;
    },
    cleanAssignmentDescription(html) {
      if (html == null) return '';
      let withLineBreaks = String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<\/?p>/gi, '\n');
      for (let i = 0; i < 5; i += 1) {
        const next = he.decode(withLineBreaks);
        if (next === withLineBreaks) break;
        withLineBreaks = next;
      }
      const cleanText = sanitizeHtml(withLineBreaks, { allowedTags: [], allowedAttributes: {} });
      return he.decode(cleanText).trim();
    },
    queueSchedulePrefetch() {
      if (this.schedulePrefetchQueued) return;
      this.schedulePrefetchQueued = true;

      const runPrefetch = () => {
        this.schedulePrefetchQueued = false;
        this.prefetchSchedulesForAssignments();
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(runPrefetch, { timeout: 1500 });
      } else {
        setTimeout(runPrefetch, 0);
      }
    },
    prefetchSchedulesForAssignments() {
      if (!this.assignments.length) return;

      const uniqueDates = new Set();
      for (const assignment of this.assignments) {
        const dateStr = assignment?.date_due?.split('T')[0];
        if (dateStr) uniqueDates.add(dateStr);
      }

      for (const dateStr of uniqueDates) {
        if (!Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
          this.fetchScheduleForDate(dateStr);
        }
      }
    },
    async fetchScheduleForDate(dateStr) {
      // Check cache first
      if (Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
        return this.schedulesCache[dateStr];
      }

      if (this.scheduleRequests[dateStr]) {
        return this.scheduleRequests[dateStr];
      }

      // Fetch schedule for the specific date
      const request = axios.get(backendUrl('/schedule'), {
        params: { start_date: dateStr, end_date: dateStr },
        headers: { Authorization: `Bearer ${this.token}` },
      })
          .then((response) => {
            this.schedulesCache[dateStr] = response.data.value || [];
            return this.schedulesCache[dateStr];
          })
          .catch((error) => {
            console.error('Error fetching schedule for date:', dateStr, error);
            return [];
          })
          .finally(() => {
            delete this.scheduleRequests[dateStr];
          });

      this.scheduleRequests[dateStr] = request;
      return request;
    },
    getClassStartTimeFromSchedules(schedules, sectionName) {
      // Try to match the assignment's section_name to a schedule's course_title
      // Use case-insensitive matching and handle partial matches
      const normalizedSectionName = sectionName.toLowerCase().trim();

      for (const schedule of schedules) {
        const normalizedCourseTitle = (schedule.course_title || '').toLowerCase().trim();
        // Check for exact match or if one contains the other
        if (normalizedCourseTitle === normalizedSectionName ||
            normalizedCourseTitle.includes(normalizedSectionName) ||
            normalizedSectionName.includes(normalizedCourseTitle)) {
          return this.formatTime(schedule.start_time);
        }
      }
      return null;
    },
    async openModal(assignment) {
      const cleanedDescription = this.cleanAssignmentDescription(assignment.long_description);
      const assignmentData = { ...assignment, long_description: cleanedDescription, classStartTime: null };

      this.currentAssignment = assignmentData;
      this.showModal = true;

      // Get the due date in YYYY-MM-DD format
      const dueDateStr = assignment.date_due.split('T')[0];

      // Try to get class start time from cached schedules or fetch if needed
      const schedules = await this.fetchScheduleForDate(dueDateStr);
      const classStartTime = this.getClassStartTimeFromSchedules(schedules, assignment.section_name);

      if (this.currentAssignment &&
          this.currentAssignment.assignment_id === assignment.assignment_id) {
        this.currentAssignment = { ...this.currentAssignment, classStartTime };
      }
    },
    closeModal() {
      this.showModal = false;
      this.currentAssignment = null;
    },
    openAddTaskModal() {
      this.editingAssignment = null;
      this.showAddTaskModal = true;
    },
    editAssignment(assignment) {
      if (!assignment.user_task) return;
      this.editingAssignment = { ...(this.assignments.find(item => item.assignment_id === assignment.assignment_id) || assignment) };
      this.closeModal();
      this.showAddTaskModal = true;
    },
    closeAddTaskModal() {
      this.showAddTaskModal = false;
      this.editingAssignment = null;
    },
    onAssignmentSaved(assignment) {
      const index = this.assignments.findIndex(item => item.assignment_id === assignment.assignment_id);
      if (index === -1) this.assignments.push(assignment);
      else this.assignments.splice(index, 1, assignment);
      this.closeAddTaskModal();
    },
    async deleteAssignment(assignment) {
      const result = await Swal.fire({
        ...this.swalDarkModeConfig,
        icon: 'warning',
        title: 'Delete Assignment?',
        text: 'Are you sure you want to delete this assignment?',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        customClass: {
          ...this.swalDarkModeConfig.customClass,
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg'
        }
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete(
            backendUrl(`/custom_assignments/${assignment.assignment_id}`),
            { headers: { Authorization: `Bearer ${this.token}` } }
        );

        this.assignments = this.assignments.filter(
            (a) => a.assignment_id !== assignment.assignment_id
        );
      } catch (error) {
        console.error('Error deleting assignment:', error);
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while deleting the assignment. Please try again.'
        });
      }
    },
    async updateStatus(assignment, status) {
      assignment.status = status;

      try {
        await axios.post(
            backendUrl('/assignment_statuses'),
            { assignment_id: assignment.assignment_id, status },
            { headers: { Authorization: `Bearer ${this.token}` } }
        );

        await this.fetchAssignments(false);
      } catch (error) {
        console.error('Error updating assignment status:', error);
      }
    },
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    pluralize(count, singularVerbPhrase, pluralVerbPhrase) {
      return count === 1 ? singularVerbPhrase : pluralVerbPhrase;
    },

  },
  mounted() {
    this.extractToken();
    this.fetchAssignments();
  },
  beforeUnmount() {
    if (this.anyModalOpen) this.unlockBodyScroll();
  },
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #475569;
}

.dark::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.dark::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

/* Make the date picker icon visible in dark mode */
.dark input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

/* Modal description scrollbar */
.desc-scroll::-webkit-scrollbar {
  width: 6px;
}
.desc-scroll::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 4px;
}
.dark .desc-scroll::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.desc-scroll {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
}
</style>
